/**
 * @file Notifications data structures and helpers.
 * Defines types mirroring public.notifications and utilities for UI logic.
 */

/** Notification category distinguishing normal notifications from alarms. */
export type NotificationKind = 'notification' | 'alarm';

/** Severity levels used for filtering, rendering and sound mapping. */
export type NotificationSeverity = 'info' | 'warning' | 'error' | 'critical';

/** Originating entity type for deep-link routing and UI markers. */
export type NotificationEntityType = 'asset' | 'companion' | 'system';

/**
 * Row shape for public.notifications as exposed via Supabase.
 * Dates are ISO strings; JSON payload is loosely typed to allow flexibility.
 */
export interface NotificationRow {
  /** Unique identifier (UUID v4/pgcrypto) for the notification. */
  id: string;
  /**
   * Target user who owns this notification (auth.users.id).
   * Client apps should treat this as server-owned: inserts typically come from an Edge Function
   * using service role and set this to the signed-in user's id. Used by RLS for SELECT/UPDATE.
   */
  user: string;
  /**
   * Multitenancy scoping: the customer/company this notification belongs to.
   * Usually derived from the current user's membership (get_current_user_customer_id()).
   * Helpful for cross-tenant admin/audit queries on the backend.
   */
  customer: number | null;
  /**
   * Who originated the notification (auth.users.id). May be null for system-generated events.
   * RLS uses this to allow only the sender to hard-delete (if enabled). In the UI, show the
   * delete action only when currentUser.id === sender.
   */
  sender: string | null;
  kind: NotificationKind;
  severity: NotificationSeverity;
  /** Short, user-visible heading. */
  title: string;
  /** Optional, longer description or message body (markdown/plain text). */
  body: string | null;
  /** Structured payload with extra context (links, thresholds, actor, etc.). */
  data: Record<string, unknown>;
  /**
   * Polymorphic source type for deep links and UI markers. 'system' indicates no entity linkage.
   */
  entity_type: NotificationEntityType;
  /**
   * Polymorphic source id. Required when entity_type !== 'system'. Typically an asset/companion id.
   */
  entity_id: string | null;
  /** Fast toggle for unread/read state; preferred for quick badge queries. */
  is_read: boolean;
  /** Timestamp when the user read the notification; null when unread. */
  read_at: string | null;
  /** When the notification becomes visible to the user (supports scheduling). */
  visible_at: string;
  /** Optional expiration after which the notification should be hidden/ignored. */
  expires_at: string | null;
  /**
   * Optional de-duplication key. When set, (user, dedup_key) is unique to avoid spamming
   * the same message repeatedly. Example: `${entity_type}:${entity_id}:alarm:${code}`.
   */
  dedup_key: string | null;
  /** Row creation time (UTC ISO). */
  created_at: string;
  /** Row update time (UTC ISO), maintained by trigger. */
  updated_at: string;
}

/** Common query options for pagination/sorting of notifications. */
export interface NotificationQueryOptions {
  /** 1-based page index for pagination. */
  page?: number;
  /** Page size (items per page). Recommend 20–50 for dropdowns/lists. */
  limit?: number;
  /** If true, restrict results to unread notifications only. */
  onlyUnread?: boolean;
  /** Optional search term to filter by title or body (case-insensitive, server-side). */
  search?: string;
}

/** UI intent for actions a user can take from a notification. */
export interface NotificationAction {
  /** Human-readable label for the CTA (e.g., "Open asset"). */
  label: string;
  /** Optional icon name used by the UI library. */
  icon?: string;
  /** Named application route to navigate to when action is performed. */
  route?: string;
  /** Route params to pass when navigating. */
  params?: Record<string, string>;
  /** Optional imperative handler when navigation is not sufficient. */
  run?: () => void | Promise<void>;
}

/** Internal mapping of sounds to play per kind/severity. */
export type NotificationSoundKey = 'alarm_critical' | 'alarm_error' | 'notification_generic';

/**
 * Resolves a sound key for a notification based on kind and severity.
 * @param kind Notification kind
 * @param severity Notification severity
 * @returns NotificationSoundKey suggested for this notification
 */
export function resolveSoundKey(
  kind: NotificationKind,
  severity: NotificationSeverity,
): NotificationSoundKey {
  if (kind === 'alarm') {
    return severity === 'critical' ? 'alarm_critical' : 'alarm_error';
  }
  return 'notification_generic';
}

/** Convenience predicates for UI logic. */
export function isAlarm(n: Pick<NotificationRow, 'kind'>): boolean {
  return n.kind === 'alarm';
}

export function isUnread(n: Pick<NotificationRow, 'is_read'>): boolean {
  return !n.is_read;
}

/** Permissions helpers aligned with RLS policies. */
export function canUpdateNotification(
  n: Pick<NotificationRow, 'user'>,
  currentUserId: string,
): boolean {
  return n.user === currentUserId;
}

export function canDeleteNotification(
  n: Pick<NotificationRow, 'sender'>,
  currentUserId: string,
): boolean {
  return n.sender === currentUserId;
}

/**
 * Ensures a JSONB value is materialized as an object.
 * Accepts stringified JSON, null/undefined, or plain objects.
 * Returns an empty object for invalid or unsupported inputs.
 *
 * @param value Unknown JSONB value as returned by the client
 * @returns Parsed object suitable for NotificationRow.data
 */
export function normalizeNotificationDataValue(value: unknown): Record<string, unknown> {
  if (value == null) return {};
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return typeof parsed === 'object' && parsed !== null
        ? (parsed as Record<string, unknown>)
        : {};
    } catch {
      return {};
    }
  }
  if (typeof value === 'object') {
    return value as Record<string, unknown>;
  }
  return {};
}

/**
 * Normalizes a notification-like row so that `data` is always an object.
 * Keeps all other fields untouched.
 *
 * @param row A row that contains a `data` field of unknown type
 * @returns The same row with `data` coerced to an object
 */
export function normalizeNotificationRow<T extends { data: unknown }>(
  row: T,
): T & { data: Record<string, unknown> } {
  return { ...row, data: normalizeNotificationDataValue(row.data) };
}

/**
 * Vectorized version of normalizeNotificationRow for arrays.
 */
export function normalizeNotificationRows<T extends { data: unknown }>(
  rows: T[],
): Array<T & { data: Record<string, unknown> }> {
  return rows.map((r) => normalizeNotificationRow(r));
}
